import { zodResolver } from "@hookform/resolvers/zod";
import { LoadingButton } from "@mui/lab";
import { Button, DialogActions, DialogContent } from "@mui/material";
import { enqueueSnackbar } from "notistack";
import { FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

import CompanionsField from "@/components/CompanionsField";
import { PassengerCard } from "@/components/PassengerCard";
import { useNewSaveDataGridChanges } from "@/requests/transport.mutations";
import { CompanionsFormSchema, companionsFormSchema } from "@/schemas/companions";
import { usePlanActions, usePlanShowUpdateCompanionsDialog } from "@/stores/use-plan-table";
import { PassengerTypes } from "@/types/global";

function UpdateCompanionsDialog() {
    const { t } = useTranslation();
    const passenger = usePlanShowUpdateCompanionsDialog();
    const { updateShowUpdateCompanionsDialog } = usePlanActions();
    const { isPending: loading, mutateAsync } = useNewSaveDataGridChanges();

    const methods = useForm({
        defaultValues: {
            companions: (passenger && passenger.fullCompanions) || [],
        },
        disabled: loading,
        resolver: zodResolver(companionsFormSchema),
    });
    const { handleSubmit } = methods;

    const createUser = async (data: CompanionsFormSchema) => {
        if (loading || !passenger?.id) return;

        try {
            const [res] = await mutateAsync({
                changes: [
                    {
                        id: passenger.id,
                        companions: data.companions,
                    },
                ],
            });
            const message = res?.message || "";
            if (res?.ok) {
                handleClose();
                enqueueSnackbar(message, {
                    variant: "success",
                });
            } else {
                enqueueSnackbar(message, {
                    variant: "error",
                });
            }
        } catch (error) {
            console.error("User error:", error);
        }
    };
    const handleClose = () => {
        updateShowUpdateCompanionsDialog();
    };

    if (!passenger) return null;
    return (
        <FormProvider {...methods}>
            <DialogContent dividers>
                <PassengerCard
                    className="gap-2 text-sm"
                    name={passenger.name}
                    isCustom={passenger.passengerType === PassengerTypes.MANUAL}
                    cancelled={passenger.cancelled}
                />

                <form
                    autoComplete="off"
                    onSubmit={handleSubmit(createUser)}
                    className="grid grid-cols-7 px-3 mt-4 gap-7"
                >
                    <CompanionsField<CompanionsFormSchema>
                        handleResetLatency={() => void 0}
                        formPath="companions"
                        loading={loading}
                    />
                </form>
            </DialogContent>
            <DialogActions>
                <Button disabled={loading} autoFocus color="secondary" variant="outlined" onClick={handleClose}>
                    {t("buttons.cancel")}
                </Button>

                <LoadingButton
                    loading={loading}
                    type="submit"
                    variant="contained"
                    color="primary"
                    onClick={handleSubmit(createUser)}
                >
                    {t("buttons.update")}
                </LoadingButton>
            </DialogActions>
        </FormProvider>
    );
}

export default UpdateCompanionsDialog;
